import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  getResolvedSettings,
  patchSettings,
  type SettingsCollectionResponse,
} from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';
import { useWorkspaceId } from '@heidi/workspace/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_LANDING_PAGE,
  isLandingPage,
  type LandingPage,
} from './landing-page-options';

// UNCARVED DOMAIN KEY (settings, MUL-3717): settings has no data package;
// mirrors the Work home mode setting hooks in `@heidi/agents/data` until the
// settings catalog hooks carve out.
// eslint-disable-next-line @heidi/rules/no-new-cache-key-declaration -- settings API key, not a TanStack factory yet (MUL-3717)
export const DEFAULT_LANDING_PAGE_SETTING_KEY = 'ui-homepage';
// UNCARVED DOMAIN KEY (settings, MUL-3717): react-query identity for ui-homepage until settings carve out.
// eslint-disable-next-line @heidi/rules/no-new-cache-key-declaration -- react-query identity for ui-homepage (MUL-3717)
export const GET_DEFAULT_LANDING_PAGE_KEY = 'getDefaultLandingPageSetting';

/**
 * The preference is held per user × workspace, so the key carries the workspace
 * id. Today this is belt-and-braces: a workspace switch hard-reloads the
 * document and this key is not in the persister's `PERSISTED_QUERY_KEYS`
 * allowlist (`src/utils/reactQueryPersistConfig.ts`), so nothing survives the
 * reload anyway. Scoping still costs nothing and holds if either of those
 * changes (an in-tab switch, or adding the key to the allowlist so MUL-3718 can
 * route without a round-trip).
 */
// UNCARVED DOMAIN KEY (settings, MUL-3717): workspace-scoped factory over the identity above until settings carve out.
// eslint-disable-next-line @heidi/rules/no-new-cache-key-declaration -- workspace-scoped factory over GET_DEFAULT_LANDING_PAGE_KEY (MUL-3717)
export const getDefaultLandingPageQueryKey = (workspaceId: string | null) =>
  [GET_DEFAULT_LANDING_PAGE_KEY, workspaceId] as const;

/** Where the change was made; lands in `settings_updated.entry_point`. */
export type DefaultLandingPageChangeSource = 'settings' | 'tooltip' | 'banner';

/**
 * The user's own choice, or `null` when they have never chosen. The backend
 * resolves the catalog default (`home`, `source: "DEFAULT"`) for an unset user;
 * that is reported as `null` so callers can tell a choice from the default —
 * the Settings row falls back to `DEFAULT_LANDING_PAGE`, the boot resolver
 * (MUL-3718) falls through to the product default.
 */
function toLandingPage(data: SettingsCollectionResponse): LandingPage | null {
  const setting = data.settings[DEFAULT_LANDING_PAGE_SETTING_KEY];
  if (!setting || setting.source === 'DEFAULT') return null;
  return isLandingPage(setting.value) ? setting.value : null;
}

/**
 * The user's chosen landing page for the active workspace, from the layered
 * settings API; `null` when they have never chosen (see `toLandingPage`).
 *
 * Scoped to the workspace by the `Heidi-Workspace-Id` header the axios client
 * already sends, and cached under that workspace's key.
 *
 * API: GET /api/v2/ml-scribe/settings?keys=ui-homepage
 */
export function useQueryDefaultLandingPage(enabled = true) {
  const workspaceId = useWorkspaceId();
  return useQuery<LandingPage | null, Error>({
    queryKey: getDefaultLandingPageQueryKey(workspaceId),
    enabled,
    queryFn: async () => {
      const { data } = await getResolvedSettings([DEFAULT_LANDING_PAGE_SETTING_KEY]);
      return toLandingPage(data);
    },
  });
}

/**
 * Persist the default landing page at the user × workspace layer and track the
 * change. Headless: toasts and copy swaps are the caller's reaction, so the
 * Settings row and the "Scribe is still here" tooltip (DEV-13207) can differ.
 *
 * API: PATCH /api/v2/ml-scribe/settings
 */
export function useMutateDefaultLandingPage({
  source,
}: {
  source: DefaultLandingPageChangeSource;
}) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  const queryKey = getDefaultLandingPageQueryKey(workspaceId);

  return useMutation<
    LandingPage | null,
    Error,
    LandingPage,
    { previous: LandingPage | null | undefined }
  >({
    mutationFn: async (landingPage) => {
      const { data } = await patchSettings({
        [DEFAULT_LANDING_PAGE_SETTING_KEY]: landingPage,
      });
      return toLandingPage(data);
    },
    // Optimistic: the Select is fully controlled from this cache, so without
    // this it snaps back to the old page for the whole round-trip.
    onMutate: async (landingPage) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<LandingPage | null>(queryKey);
      queryClient.setQueryData(queryKey, landingPage);
      return { previous };
    },
    onError: (error, requested, context) => {
      queryClient.setQueryData(queryKey, context?.previous ?? null);
      // A 422 (setting unknown because ml-scribe#19814 is not deployed here) or
      // a 403 (workspace header mismatch) would otherwise look like nobody using
      // the feature. Closed enums only — no PHI.
      logger.warn('[MUL-3717] default landing page persist failed', {
        attempted: requested,
        entry_point: source,
        error: sanitizeError(error),
      });
    },
    onSuccess: (landingPage, requested, context) => {
      queryClient.setQueryData(queryKey, landingPage ?? requested);
      track({
        event_name: TrackingEventName.SettingsUpdated,
        props: {
          update_action: 'default_landing_page_changed',
          setting_value: landingPage ?? requested,
          // What the user saw before: an unset user sees the product default.
          previous_value: context?.previous ?? DEFAULT_LANDING_PAGE,
          entry_point: source,
        },
        options: { amplitude: false },
      });
    },
  });
}
